import {
  useCreatePlaybookMutation,
  useUpdatePlaybookMutation,
} from '@carboncopyco/data';
import type { Playbook, Workspace } from '@carboncopyco/types';
import { Button, ConfirmAlert, Loading, Modal, notify } from '@carboncopyco/ui';
import { usePlaybookAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useParams } from 'react-router-dom';
import { AssetSelectionPanel } from './AssetSelectionPanel';
import { usePlaybookModalState } from './PlaybookModalContext';
import { PlaybookPanel } from './PlaybookPanel';
import { PlaybookTitle } from './PlaybookTitle';

export type PlaybookModalProps = {
  onClose: () => void;
  onSuccess?: (playbookId: Playbook['id'], workspace?: Workspace['id']) => void;
};
export const PlaybookModal = ({ onClose, onSuccess }: PlaybookModalProps) => {
  const flags = useFlags();
  const [showConfirmDiscardModal, setShowConfirmDiscardModal] = useBoolean();
  const { organizationId, folderId } = useParams();
  const {
    assets,
    playbookName,
    workspaceId,
    playbookNameError,
    isEditingExternalLink,
    playbookId,
    isInitialState,
    multiTab,
  } = usePlaybookModalState();
  const {
    trackSubmitCreatePlaybook,
    trackSubmitUpdatePlaybook,
    trackDiscardPlaybookChanges,
  } = usePlaybookAnalytics(
    playbookId ? 'update_playbook_modal' : 'create_playbook_modal'
  );
  const handleDiscard = () => {
    trackDiscardPlaybookChanges({ playbookId });
    onClose();
  };
  const [createPlaybook, { isLoading: isCreatingPlaybook }] =
    useCreatePlaybookMutation();
  const [updatePlaybook, { isLoading: isUpdatingPlaybook }] =
    useUpdatePlaybookMutation();
  const isLoading = isCreatingPlaybook || isUpdatingPlaybook;

  const handleUpdatePlaybook = () => {
    trackSubmitUpdatePlaybook({ playbookId });
    updatePlaybook({
      organizationId,
      workspaceId,
      id: playbookId,
      assets,
      name: playbookName,
      playSettings: flags.multiTabDisplay ? { multiTab } : undefined,
    })
      .unwrap()
      .then(({ id, workspaceId }) => {
        notify.success('Successfully updated the playbook');
        onSuccess?.(id, workspaceId);
      })
      .catch(() => {
        notify.error('Error updating the playbook');
      })
      .finally(() => {
        onClose();
      });
  };

  const handleCreatePlaybook = () => {
    trackSubmitCreatePlaybook({});
    createPlaybook({
      organizationId,
      workspaceId,
      folderId,
      assets,
      name: playbookName,
      playSettings: flags.multiTabDisplay ? { multiTab } : undefined,
    })
      .unwrap()
      .then(({ id }) => {
        onSuccess?.(id, workspaceId);
      })
      .catch(() => {
        notify.error('Error creating a playbook');
      })
      .finally(() => {
        onClose();
      });
  };

  const submitLabel = playbookId ? 'Update playbook' : 'Create playbook';
  const discardMessage = playbookId
    ? 'If you leave now, your updates will be lost.'
    : "If you leave, this Playbook won't be saved";

  return (
    <>
      <Modal
        open
        onClose={
          isInitialState ? handleDiscard : setShowConfirmDiscardModal.setTrue
        }
        closeOnlyFromCloseIcon
        title={<PlaybookTitle />}
      >
        <div
          className="flex gap-3 mb-4"
          data-ds-id="playbook-modal-content"
        >
          <AssetSelectionPanel />
          <PlaybookPanel />
        </div>
        <div className="flex justify-end gap-3">
          <Button
            secondary
            label="Cancel"
            onClick={
              isInitialState
                ? handleDiscard
                : setShowConfirmDiscardModal.setTrue
            }
          />
          <Button
            onClick={playbookId ? handleUpdatePlaybook : handleCreatePlaybook}
            disabled={
              !assets.length ||
              isLoading ||
              !playbookName ||
              !!playbookNameError ||
              isEditingExternalLink ||
              isInitialState
            }
          >
            <div className="w-18 flex items-center justify-center">
              {isLoading ? (
                <Loading color="text-ds-primary-500" />
              ) : (
                submitLabel
              )}
            </div>
          </Button>
        </div>
      </Modal>
      <ConfirmAlert
        danger
        show={showConfirmDiscardModal}
        title="Are you sure you want to leave?"
        containerClass="w-ds-normal-modal"
        description={discardMessage}
        onConfirm={handleDiscard}
        noLabel="Keep editing"
        yesLabel="Leave"
        onClose={setShowConfirmDiscardModal.setFalse}
      />
    </>
  );
};
